import { Facebook, Instagram, Youtube } from "lucide-react";
import arcAuthorised from "@/assets/arc-authorised.png";

const SiteFooter = () => {
  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "Discover ACG", href: "/about" },
    { label: "Our Services", href: "/services" },
    { label: "Projects", href: "/about" },
    { label: "Contact Us", href: "/about" },
    { label: "Brands", href: "/brands" },
    { label: "Area We Service", href: "/about" },
    { label: "Aircon Care", href: "/services" },
  ];

  const services = [
    { label: "Commercial", href: "/services/commercial" },
    { label: "Residential", href: "/services/residential" },
    { label: "Ductwork Replacement Repairs", href: "/services/ductwork-replacement-repairs" },
    { label: "Corrosion Protection", href: "/services/corrosion-protection" },
    { label: "Home Automation", href: "/services/home-automation" },
    { label: "Leak Repairs", href: "/services/leak-repairs" },
    { label: "Rejuvenation Eco Clean", href: "/services/rejuvenation-eco-clean" },
    { label: "Air Conditioning Replacements", href: "/services/air-conditioning-replacements" },
    { label: "Air Conditioning Installation", href: "/services/air-conditioning-installation" },
    { label: "Air Conditioning Repairs", href: "/services/air-conditioning-repairs" },
    { label: "Air Conditioning Maintenance", href: "/services/air-conditioning-maintenance" },
    { label: "Water Cooler", href: "/services/water-cooler" },
  ];

  const acTypes = [
    { label: "Split System", href: "/ac-types/split-system" },
    { label: "Ducted System", href: "/ac-types/ducted-system" },
    { label: "Multi-Air System", href: "/ac-types/multi-air-system" },
    { label: "Bulk Head AC Units", href: "/ac-types/bulk-head-ac-units" },
    { label: "Floor Heating System", href: "/ac-types/floor-heating-system" },
    { label: "Package Air Conditioning Unit", href: "/ac-types/package-air-conditioning-unit" },
    { label: "Ceiling Cassette", href: "/ac-types/ceiling-cassette" },
    { label: "Suspended Cassette", href: "/ac-types/suspended-cassette" },
    { label: "VRV System", href: "/ac-types/vrv-system" },
    { label: "Window Wall AC System", href: "/ac-types/window-wall-ac-system" },
    { label: "Evaporative Cooling", href: "/ac-types/evaporative-cooling" },
    { label: "Gas Ducted Heating Systems", href: "/ac-types/gas-ducted-heating-systems" },
  ];

  const discoverACG = [
    { label: "Our Process", href: "/our-process" },
    { label: "Testimonials", href: "/about" },
    { label: "Projects", href: "/about" },
    { label: "Interest-Free Payment Plans", href: "/finance-options" },
  ];

  const support = [
    { label: "Contact Us", href: "/about" },
    { label: "Recruitment", href: "/about" },
    { label: "Blogs", href: "/blog" },
    { label: "Terms & Conditions", href: "/about" },
    { label: "Privacy Policies", href: "/about" },
    { label: "FAQ's", href: "/faq" },
    { label: "Membership", href: "/membership" },
    { label: "Finance", href: "/finance-options" },
  ];

  const FooterColumn = ({ title, links, extra }: { title: string; links: { label: string; href: string }[]; extra?: React.ReactNode }) => (
    <div>
      <h4 className="text-secondary font-bold text-sm mb-4">{title}</h4>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
      {extra}
    </div>
  );

  return (
    <>
      {/* Main Footer */}
      <footer className="bg-acg-charcoal pt-12 pb-8 px-4">
        <div className="container mx-auto">
          {/* Link Columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
            <FooterColumn title="Quick Links" links={quickLinks} />
            <FooterColumn title="Services" links={services} />
            <FooterColumn title="AC Types" links={acTypes} />
            <FooterColumn title="Discover ACG" links={discoverACG} />
            <FooterColumn title="Support" links={support} extra={
              <div className="mt-6">
                <img src={arcAuthorised} alt="Australian Refrigeration Council Authorised — ARC RTA AU38134 certification badge" className="w-44 rounded-lg shadow-lg" width={176} height={100} loading="lazy" />
                <p className="text-sm font-bold text-secondary mt-3 tracking-wide">ARC RTA AU38134</p>
              </div>
            } />
          </div>

          {/* ARC Badge row */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-primary-foreground/10 pt-6">
            <p className="text-primary-foreground font-bold text-lg">
              Authorised Daikin, Fujitsu & Mitsubishi Heavy Industries Dealer — Southern Highlands NSW
            </p>
            <div className="flex items-center gap-6">
              <a href="https://facebook.com/acgsouthernhighlands" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 flex items-center gap-1.5 text-sm">
                <Facebook className="w-4 h-4" /> @acgsouthernhighlands
              </a>
              <a href="https://instagram.com/acg_southernhighlands" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 flex items-center gap-1.5 text-sm">
                <Instagram className="w-4 h-4" /> @acg_southernhighlands
              </a>
              <a href="https://youtube.com/@acgsouthernhighlands" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 flex items-center gap-1.5 text-sm">
                <Youtube className="w-4 h-4" /> @acg southern highlands
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom Copyright Bar */}
      <div className="bg-acg-light-blue py-4 px-4">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-4">
          <a href="/">
            <img src="/acg-logo.webp" alt="ACG Southern Highlands logo" className="h-12 w-auto" width={160} height={48} loading="lazy" />
          </a>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Copyright {new Date().getFullYear()}</span>
            <span>/</span>
            <span>ACG Southern Highlands</span>
            <span>/</span>
            <span>All Rights Reserved</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default SiteFooter;
